import { CompanionService, ChatService, UserService, AuthService, AudioService } from './types';
import { appConfig, isMockMode } from '../../config/app-config';

// Mock implementations
import { CompanionServiceMock } from './mock/companion-service-mock';
import { ChatServiceMock } from './mock/chat-service-mock';
import { UserServiceMock } from './mock/user-service-mock';
import { AuthServiceMock } from './mock/auth-service-mock';
import { AudioServiceMock } from './mock/audio-service-mock';

// Real API implementations
import { CompanionServiceReal } from './real/companion-service-real';
import { ChatServiceReal } from './real/chat-service-real';
import { UserServiceReal } from './real/user-service-real';
import { AuthServiceReal } from './real/auth-service-real';
import { AudioServiceReal } from './real/audio-service-real';

export interface ServiceContainer {
  companionService: CompanionService;
  chatService: ChatService;
  userService: UserService;
  authService: AuthService;
  audioService: AudioService;
}

class ServiceFactory {
  private services: ServiceContainer | null = null;

  getServices(): ServiceContainer {
    if (!this.services) {
      this.services = this.createServices();
    }
    return this.services;
  }

  private createServices(): ServiceContainer {
    if (appConfig.enableDebugLogs) {
      console.log(`🏭 ServiceFactory: Creating ${appConfig.apiMode} services`);
    }

    if (isMockMode()) {
      return {
        companionService: new CompanionServiceMock(),
        chatService: new ChatServiceMock(),
        userService: new UserServiceMock(),
        authService: new AuthServiceMock(),
        audioService: new AudioServiceMock(),
      };
    } else {
      return {
        companionService: new CompanionServiceReal(),
        chatService: new ChatServiceReal(),
        userService: new UserServiceReal(),
        authService: new AuthServiceReal(),
        audioService: new AudioServiceReal(),
      };
    }
  }

  // Method to force recreation of services (useful for switching modes)
  resetServices(): void {
    if (appConfig.enableDebugLogs) {
      console.log('🔄 ServiceFactory: Resetting services');
    }
    this.services = null;
  }

  // Method to get current mode info
  getCurrentMode(): {
    mode: 'mock' | 'real';
    baseUrl?: string;
    debugLogs: boolean;
  } {
    return {
      mode: appConfig.apiMode,
      baseUrl: appConfig.apiMode === 'real' ? appConfig.apiBaseUrl : undefined,
      debugLogs: appConfig.enableDebugLogs,
    };
  }
}

// Export singleton instance
export const serviceFactory = new ServiceFactory();
